import { uniqueId } from 'lodash';
import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import { AskNovoAiDrawerState } from 'institutions/components/ask-novoai/helpers';
import { AdditionalResource, AdditionalResourceStatus, HelpOptions } from 'redux/schemas/models/help-options';
import {
  getHelpOptionsSettings,
  openNovoAIDialog,
  closeNovoAIDialog,
  changeDrawerState,
  askNovoAiRequest,
  getRecentQuestions,
  setVoteLoading,
  setVoteState,
  showVoteFeedback,
  resetVoteState,
  resetState,
  addAdditionalResourceFiles,
  updateAdditionalResource,
  removeAdditionalResource,
} from 'redux/actions/help-options';
import { initialRootState } from './index';

export default createReducer(initialRootState, builder => {
  builder.addCase(resetState.fulfilled, (state) => {
    state.app.askNovoAi = initialRootState.app.askNovoAi;
  });
  builder.addCase(getHelpOptionsSettings.fulfilled, (state, action: PayloadAction<HelpOptions>) => {
    state.app.askNovoAi.helpOptions = action.payload;
    if (state.app.askNovoAi.helpOptions) {
      state.app.askNovoAi.helpOptions.hasAdditionalResourcesChanged = false;
    }
    // Processing Files
    const { embedDataRequests, additionalResources } = action.payload;
    if (!embedDataRequests || !additionalResources) return;

    const unprocessedRequests = embedDataRequests.filter(
      request => !request.completed && (request?.failedFiles?.length > 0 || request?.processedFiles?.length > 0),
    );

    if (unprocessedRequests.length === 0) return;

    // Build Sets for lookup
    const failedFileIds = new Set(unprocessedRequests.flatMap(request => request.failedFiles || []));
    const processingFileIds = new Set(unprocessedRequests.flatMap(request => request.processedFiles || []));

    const additionalResourcesWithStatus = additionalResources.map(resource => {
      let status = AdditionalResourceStatus.PROCESSED;

      if (processingFileIds.has(resource.id)) {
        status = AdditionalResourceStatus.PROCESSING;
      } else if (failedFileIds.has(resource.id)) {
        status = AdditionalResourceStatus.FAILED;
      }

      return { ...resource, status };
    });

    state.app.askNovoAi.helpOptions.additionalResources = additionalResourcesWithStatus;
  });
  builder.addCase(openNovoAIDialog, (state) => {
    state.app.askNovoAi.isOpen = true;
  });
  builder.addCase(closeNovoAIDialog, (state) => {
    state.app.askNovoAi.isOpen = false;
  });
  builder.addCase(changeDrawerState, (state, payload) => {
    state.app.askNovoAi.drawerState = payload.payload;
  });
  builder.addCase(askNovoAiRequest.fulfilled, (state, { payload }) => {
    state.app.askNovoAi.response = payload;
    if (payload.responseCode === 200 || payload.responseCode === 409) {
      state.app.askNovoAi.drawerState = AskNovoAiDrawerState.SUCCESS;
    } else {
      state.app.askNovoAi.response.answer = '';
      state.app.askNovoAi.drawerState = AskNovoAiDrawerState.ERROR;
    }
  });
  builder.addCase(askNovoAiRequest.pending, (state) => {
    state.app.askNovoAi.drawerState = AskNovoAiDrawerState.LOADING;
  });
  builder.addCase(askNovoAiRequest.rejected, (state) => {
    // Mostly for network errors
    state.app.askNovoAi = {
      ...state.app.askNovoAi,
      drawerState: AskNovoAiDrawerState.ERROR,
      response: {
        questionText: state.app.askNovoAi.question,
        answer: '',
        responseCode: 500,
        referencedComponents: [],
        liveEvents: [],
      },
    };
  });
  builder.addCase(getRecentQuestions.fulfilled, (state, payload) => {
    state.app.askNovoAi.recentQuestions = payload.payload;
  });

  // Vote state handlers
  builder.addCase(setVoteLoading, (state, { payload }) => {
    const { questionId, isLoading } = payload;
    if (!state.app.askNovoAi.voteState[questionId]) {
      state.app.askNovoAi.voteState[questionId] = {
        upvoted: false,
        downvoted: false,
        isLoading: false,
        showFeedback: false,
      };
    }
    state.app.askNovoAi.voteState[questionId].isLoading = isLoading;
  });

  builder.addCase(setVoteState, (state, { payload }) => {
    const { questionId, voteType, voted } = payload;
    if (!state.app.askNovoAi.voteState[questionId]) {
      state.app.askNovoAi.voteState[questionId] = {
        upvoted: false,
        downvoted: false,
        isLoading: false,
        showFeedback: false,
      };
    }

    if (voteType === 'vote_up') {
      state.app.askNovoAi.voteState[questionId].upvoted = voted;
      state.app.askNovoAi.voteState[questionId].downvoted = false;
    } else {
      state.app.askNovoAi.voteState[questionId].downvoted = voted;
      state.app.askNovoAi.voteState[questionId].upvoted = false;
    }
    state.app.askNovoAi.voteState[questionId].isLoading = false;
  });

  builder.addCase(showVoteFeedback, (state, { payload }) => {
    const { questionId, show } = payload;
    if (!state.app.askNovoAi.voteState[questionId]) {
      state.app.askNovoAi.voteState[questionId] = {
        upvoted: false,
        downvoted: false,
        isLoading: false,
        showFeedback: false,
      };
    }
    state.app.askNovoAi.voteState[questionId].showFeedback = show;
  });

  builder.addCase(resetVoteState, (state, { payload }) => {
    const { questionId } = payload;
    state.app.askNovoAi.voteState[questionId] = {
      upvoted: false,
      downvoted: false,
      isLoading: false,
      showFeedback: false,
    };
  });

  builder.addCase(addAdditionalResourceFiles, (state, { payload }) => {
    const { files } = payload;
    if (!state.app.askNovoAi.helpOptions) {
      state.app.askNovoAi.helpOptions = {};
    }
    if (!state.app.askNovoAi.helpOptions.additionalResources) {
      state.app.askNovoAi.helpOptions.additionalResources = [];
    }

    const newResources: AdditionalResource[] = files.map((file: File) => {
      const resourceId = uniqueId('resource_');
      return {
        id: parseInt(resourceId.replace('resource_', ''), 10) || Date.now(),
        uniqueId: resourceId,
        name: file.name,
        size: file.size,
        type: file.type,
        url: '',
        status: AdditionalResourceStatus.UPLOADING,
        uploadedFile: file,
      };
    });

    state.app.askNovoAi.helpOptions.additionalResources = [
      ...state.app.askNovoAi.helpOptions.additionalResources,
      ...newResources,
    ];
    state.app.askNovoAi.helpOptions.hasAdditionalResourcesChanged = true;
  });

  builder.addCase(updateAdditionalResource, (state, { payload }) => {
    const { uniqueId: resourceUniqueId, updates } = payload;
    if (!state.app.askNovoAi.helpOptions?.additionalResources) return;

    state.app.askNovoAi.helpOptions.additionalResources = state.app.askNovoAi.helpOptions.additionalResources.map(
      (resource) => (resource.uniqueId === resourceUniqueId ? { ...resource, ...updates } : resource),
    );
    state.app.askNovoAi.helpOptions.hasAdditionalResourcesChanged = true;
  });

  builder.addCase(removeAdditionalResource, (state, { payload }) => {
    const { id: resourceUniqueId } = payload;
    if (!state.app.askNovoAi.helpOptions?.additionalResources) return;
    state.app.askNovoAi.helpOptions.additionalResources = state.app.askNovoAi.helpOptions.additionalResources.filter(
      (resource) => resource.id !== resourceUniqueId,
    );
    state.app.askNovoAi.helpOptions.hasAdditionalResourcesChanged = true;
  });
});
